<script setup lang="ts">
/**
 * ReceiveMultipleMilkDialog
 *
 * Two-step dialog for receiving multiple milk bottles in a single session.
 *
 * Step 1 — Scan Bottles:
 *   Scan or manually enter container IDs one by one.
 *   Each ID is validated (alphanumeric / dash, no duplicates) and added to
 *   a list.  Click "Next" once all bottles are scanned.
 *
 * Step 2 — Fill Details:
 *   One form row per scanned bottle.  Each row carries the same fields as
 *   the single-bottle Receive Milk dialog.  The "Apply to All" button copies
 *   every non-empty field from row 1 to all other rows; expiry dates are
 *   automatically recalculated per-row using the hospital's configured
 *   shelf-life rules.  Click "Receive" to emit the array of payloads.
 */
import { DialogModal, useToaster, Icon, DataTable } from '@tms-phoenix/ui-vue';
import Button from 'primevue/button';
import InputText from 'primevue/inputtext';
import InputNumber from 'primevue/inputnumber';
import Select from 'primevue/select';
import Tag from 'primevue/tag';
import Column from 'primevue/column';
import AppDatePicker from '@/shared/components/ui/AppDatePicker.vue';
import ScanInput from '@/shared/components/ui/ScanInput.vue';
import type {
  MilkReceivableMilkStatus,
  MilkReceivableMilkType,
  DonorApprovalStatus,
  ReceiveMilkContainerPayload,
  ReceiveMotherMilkPayload,
  ReceiveDonorMilkPayload,
  ReceiveMultipleBottlesPayload,
} from '../types';
import { getApiClient } from '@/core/services/api.service';
import { API_ROUTES } from '@/core/config';
import {
  getExpiryOffsetHoursForStatus,
  getExpiryRows,
  getLocationBucket,
  getExpiryBaseTimestamp,
  filterLocationsByDeepFreezerRule,
  resetExpiryRulesCache,
  type ExpiryRuleRow,
} from '../services/milk-expiry-rules';
import { useLocale } from '@/shared/composables/useLocale';
import { useEnumTranslation } from '@/shared/composables/useEnumTranslation';
import { useSettingsStore } from '@/stores/settings.store';
import type { MilkTypeEntry } from '@/modules/settings/types';
import { useBarcodeScanner } from '@/shared/composables/useBarcodeScanner';
import { getMilkProviderByLabel } from '../services/milk-receivables.api';
import { formatLocationLabel, getMilkStorageTypeLabel } from '@/shared/utils/location-label';
import { useFormDirty } from '@/shared/composables/useFormDirty';
import FormDirtyDialog from '@/shared/components/feedback/FormDirtyDialog.vue';

defineOptions({ inheritAttrs: false });

const { pickerDateToUtcIso } = useTimezone();
const toast = useToaster();
const { t, locale } = useLocale();
const { milkStatusOptions, getMilkTypeLabel } = useEnumTranslation();
const settingsStore = useSettingsStore();

// ---------------------------------------------------------------------------
// Props & Emits
// ---------------------------------------------------------------------------
type Props = {
  visible: boolean;
  /** Which tab context: 'mother' or 'donor' */
  origin: 'mother' | 'donor';
};

const props = defineProps<Props>();

const emit = defineEmits<{
  'update:visible': [value: boolean];
  /** Emitted when the user confirms; carries the full bulk payload. */
  'containers-received': [payload: ReceiveMultipleBottlesPayload];
}>();

// ---------------------------------------------------------------------------
// Step management
// ---------------------------------------------------------------------------
type Step = 'scan' | 'details';
const currentStep = ref<Step>('scan');

// ---------------------------------------------------------------------------
// Shared data — storage locations, milk types, expiry rules
// ---------------------------------------------------------------------------
const locationOptions = ref<{ label: string; value: string; storageType?: string; storageLocationId?: string }[]>([]);
const isLocationLoading = ref(false);

async function fetchStorageLocations() {
  isLocationLoading.value = true;
  try {
    const api = getApiClient();
    const res = await api.get(`${API_ROUTES.SETTINGS}/storage-locations/`);
    const data = res.data?.data ?? res.data;
    if (Array.isArray(data)) {
      locationOptions.value = data
        .filter((loc: { is_active?: boolean }) => loc.is_active !== false)
        .map((loc: { name: string; storage_location_id: string; storage_type?: string }) => ({
          label: formatLocationLabel(loc.name, getMilkStorageTypeLabel(loc.storage_type)),
          value: loc.name,
          storageType: loc.storage_type,
          storageLocationId: loc.storage_location_id,
        }));
    }
  } catch {
    locationOptions.value = [];
  } finally {
    isLocationLoading.value = false;
  }
}

const milkTypeApiOptions = computed(() =>
  settingsStore.milkTypes
    .filter((mt: MilkTypeEntry) => mt.status === 'Active' && mt.typeCode !== 'OPEN')
    .map((mt: MilkTypeEntry) => ({
      label: getMilkTypeLabel(mt.typeCode) || mt.milkType,
      value: mt.typeCode,
    })),
);
const isMilkTypeLoading = computed(() => settingsStore.milkTypesLoading);

/** Hospital's MILK expiry rule rows — used to determine Deep Freezer availability (Settings > Expiry Rules). */
const milkExpiryRuleRows = ref<ExpiryRuleRow[]>([]);

async function loadMilkExpiryRuleRows() {
  try {
    milkExpiryRuleRows.value = await getExpiryRows('MILK');
  } catch {
    milkExpiryRuleRows.value = [];
  }
}

/**
 * Locations offered in a row's Location select — restricted to Fridge-type
 * storage while that row's milkStatus is THAWED (thawed milk may only be
 * stored in a Fridge), Fridge-type storage EXCLUDED while the row's
 * milkStatus is FROZEN (frozen milk must never be stored in a
 * Fridge/Refrigerator), and with Deep Freezer locations hidden whenever the
 * applicable milk type's (Mother vs Donor, from props.origin) Deep Freezer
 * expiry rule is disabled in Settings > Expiry Rules.
 */
function getLocationOptionsForRow(milkStatus: string | null): { label: string; value: string; storageType?: string; storageLocationId?: string }[] {
  let statusFiltered = locationOptions.value;
  if (milkStatus === 'THAWED') {
    statusFiltered = locationOptions.value.filter((o) => getLocationBucket(o.storageType) === 'Fridge');
  } else if (milkStatus === 'FROZEN') {
    statusFiltered = locationOptions.value.filter((o) => getLocationBucket(o.storageType) !== 'Fridge');
  }
  return filterLocationsByDeepFreezerRule(statusFiltered, props.origin === 'donor' ? 'DONOR' : 'MOM', milkExpiryRuleRows.value);
}

async function recalcExpiryForRow(index: number) {
  const row = rows.value[index];
  if (!row || !row.milkStatus || !row.pumpedDate) {
    if (row) row.expiryDate = null;
    return;
  }
  const locationOpt = locationOptions.value.find((o) => o.value === row.location);
  const locationBucket = getLocationBucket(locationOpt?.storageType);
  if (row.milkStatus !== 'THAWED' && !locationBucket) {
    row.expiryDate = null;
    return;
  }
  row.isFetchingExpiry = true;
  try {
    const offsetHours = await getExpiryOffsetHoursForStatus(props.origin, row.milkStatus, locationBucket);
    // Base timestamp per the Receive Milk Expiry Rules (Current Timestamp vs
    // Pump Date — see getExpiryBaseTimestamp; NEVER the milk batch/container
    // creation timestamp), determined from this row's Milk Status + selected
    // Location's storage type.
    const baseTimestamp = getExpiryBaseTimestamp({
      milkStatus: row.milkStatus,
      locationType: locationBucket,
      pumpDate: row.pumpedDate,
    });
    row.expiryDate = offsetHours !== null && baseTimestamp ? new Date(baseTimestamp.getTime() + offsetHours * 60 * 60 * 1000) : null;
  } finally {
    row.isFetchingExpiry = false;
  }
}

// ---------------------------------------------------------------------------
// Step 1 — Scan state
// ---------------------------------------------------------------------------

/**
 * A successfully scanned bottle entry.
 * Container ID, resolved provider details, and validated origin are captured
 * at scan time and pre-filled into the Step 2 form rows.
 */
interface ScannedBottle {
  containerId: string;
  /** Full name resolved via getMilkProviderByLabel */
  providerName: string;
  /** person_id — forwarded as providerId in the API payload */
  providerId: string | null;
  /** Origin confirmed by the API ('mother' | 'donor') */
  scannedOrigin: 'mother' | 'donor';
}

const MAX_BOTTLES = 25;

const scanInput = ref('');
const scannedBottles = ref<ScannedBottle[]>([]);
const scanInputError = ref('');
/** true while the provider-lookup API call is in progress for the current ID */
const isAdding = ref(false);
/** Result of a comma-separated batch paste — null when no batch has been run yet */
const batchResult = ref<{
  accepted: string[];
  rejected: Array<{ id: string; reason: string }>;
} | null>(null);
/**
 * Origin ('mother' | 'donor') resolved from the first successfully scanned
 * bottle in the current session. Every subsequent scan (single or batch)
 * must resolve to this same origin — a mismatch (e.g. a BOTH-type provider
 * that would otherwise be forced to a different origin) is rejected just
 * like a dialog-origin mismatch. Reset to null whenever the scanned list
 * becomes empty or the dialog resets.
 */
const firstScanOrigin = ref<'mother' | 'donor' | null>(null);

const scanner = useBarcodeScanner({ workflow: 'receive-milk-multi' });

const { markDirty, reset: resetDirty, guardClose, confirmState, onConfirm, onCancel, onVisibleUpdate } = useFormDirty();

// Mark dirty only when there are scanned bottles; clears dirty when all are removed or on reset
watch(scannedBottles, (bottles) => {
  markDirty((bottles as unknown[]).length > 0);
}, { deep: true });

/**
 * Validate the container ID and look up its provider via the same API call
 * used by the single-bottle ReceiveMilkDialog.
 * Mirrors the checks in ReceiveMilkDialog.handleScan():
 *   1. Non-empty
 *   2. Alphanumeric / dash format
 *   3. Duplicate detection
 *   4. tenantId presence
 *   5. getMilkProviderByLabel — 404, origin-mismatch, and generic errors
 */
async function addScannedBottle() {
  const id = scanInput.value.trim();

  // Comma-separated batch paste — delegate to batch handler
  if (id.includes(',')) {
    const ids = id.split(',').map((s: string) => s.trim()).filter(Boolean);
    if (ids.length) await addBatchBottles(ids);
    return;
  }

  // 1. Non-empty
  if (!id) {
    scanInputError.value = t('inventory.containerIdRequired');
    return;
  }

  // 2. Format
  if (!/^[A-Za-z0-9\-]+$/.test(id)) {
    scanInputError.value = t('inventory.containerIdAlphanumeric');
    return;
  }

  // 3. Limit
  if (scannedBottles.value.length >= MAX_BOTTLES) {
    scanInputError.value = t('inventory.bottleLimitReached', { max: MAX_BOTTLES });
    return;
  }

  // 4. Duplicate
  if (scannedBottles.value.some((b: ScannedBottle) => b.containerId === id)) {
    scanInputError.value = t('inventory.containerIdAlreadyAdded', { id });
    return;
  }

  // 5. Provider lookup
  isAdding.value = true;
  scanInputError.value = '';
  try {
    const provider = await getMilkProviderByLabel(id);

    const isBothProvider = provider.person_type === 'BOTH';
    const apiOrigin = provider.person_type === 'MOTHER' ? 'mother' : 'donor';

    // Origin mismatch — BOTH providers are accepted on either tab
    if (!isBothProvider && apiOrigin !== props.origin) {
      const found = apiOrigin === 'mother' ? t('common.mother') : t('common.donor');
      const expected = found
      const reason = t('inventory.providerMismatch', { found, expected });
      toast.danger(reason);
      batchResult.value = { accepted: [], rejected: [{ id, reason }] };
      return;
    }

    const effectiveOrigin = isBothProvider ? props.origin : apiOrigin;

    // First-scan lock — every bottle scanned in this session must resolve to
    // the same origin as the first one accepted.
    if (firstScanOrigin.value && effectiveOrigin !== firstScanOrigin.value) {
      const found = effectiveOrigin === 'mother' ? t('common.mother') : t('common.donor');
      const expected = firstScanOrigin.value === 'mother' ? t('common.mother') : t('common.donor');
      const reason = t('inventory.providerMismatch', { found, expected });
      toast.danger(reason);
      batchResult.value = { accepted: [], rejected: [{ id, reason }] };
      return;
    }

    // Same-provider check — the first scanned bottle establishes the
    // mother/donor for this session; every subsequent bottle must belong to
    // that same person, even when the origin type (mother vs donor) matches.
    if (scannedBottles.value.length > 0 && provider.person_id !== scannedBottles.value[0].providerId) {
      const label = props.origin === 'mother' ? t('common.mother') : t('common.donor');
      const reason = t('inventory.receive.differentProvider', { id, label });
      toast.danger(reason);
      batchResult.value = { accepted: [], rejected: [{ id, reason }] };
      return;
    }

    // Donor screening check: only APPROVED donors may have milk received.
    if (effectiveOrigin === 'donor' && provider.screening_status !== 'APPROVED') {
      toast.danger(t('inventory.donorScreeningNotApproved'));
      return;
    }

    scannedBottles.value.push({
      containerId: id,
      providerName: `${provider.first_name} ${provider.last_name}`,
      providerId: provider.person_id,
      scannedOrigin: effectiveOrigin,
    });
    if (!firstScanOrigin.value) firstScanOrigin.value = effectiveOrigin;
    scanInput.value = '';
    scanInputError.value = '';
  } catch (err: unknown) {
    const status = (err as { response?: { status?: number } })?.response?.status;
    if (status === 404) {
      toast.danger(t('inventory.noProviderFound'));
    } else {
      toast.danger(t('inventory.failedLookupProvider'));
    }
  } finally {
    isAdding.value = false;
  }
}

/**
 * Process a batch of container IDs (from a comma-separated paste).
 * Each ID goes through the same checks as addScannedBottle.
 * Results are stored in batchResult for display.
 */
async function addBatchBottles(ids: string[]) {
  const accepted: string[] = [];
  const acceptedBottles: ScannedBottle[] = [];
  const rejected: Array<{ id: string; reason: string }> = [];
  // Track IDs accepted in this batch run to catch within-batch duplicates
  const acceptedSet = new Set<string>(scannedBottles.value.map((b: ScannedBottle) => b.containerId));
  // Origin established by the first bottle in this session (single scan or a
  // prior batch) — every bottle in this batch must resolve to the same origin.
  let originLock = firstScanOrigin.value;
  // Provider (mother/donor) established by the first bottle in this session —
  // every bottle in this batch must belong to that same person.
  let providerLock = scannedBottles.value[0]?.providerId ?? null;

  isAdding.value = true;
  scanInputError.value = '';
  batchResult.value = null;

  for (const id of ids) {
    // Format check
    if (!/^[A-Za-z0-9\-]+$/.test(id)) {
      rejected.push({ id, reason: t('inventory.containerIdAlphanumeric') });
      continue;
    }

    // Duplicate check (existing + already accepted in this batch)
    if (acceptedSet.has(id)) {
      rejected.push({ id, reason: t('inventory.receive.alreadyAdded') });
      continue;
    }

    // Limit check — pre-existing bottles + accepted so far in this batch
    if (scannedBottles.value.length + acceptedBottles.length >= MAX_BOTTLES) {
      rejected.push({ id, reason: t('inventory.bottleLimitBatchReached', { max: MAX_BOTTLES }) });
      continue;
    }

    // Provider lookup
    try {
      const provider = await getMilkProviderByLabel(id);
      const isBothProvider = provider.person_type === 'BOTH';
      const apiOrigin = provider.person_type === 'MOTHER' ? 'mother' : 'donor';

      if (!isBothProvider && apiOrigin !== props.origin) {
        const found = apiOrigin === 'mother' ? t('common.mother') : t('common.donor');
        const expected = props.origin === 'mother' ? t('common.mother') : t('common.donor');
        rejected.push({ id, reason: t('inventory.providerMismatch', { found, expected }) });
        continue;
      }

      const effectiveOrigin = isBothProvider ? props.origin : apiOrigin;

      // First-scan lock — every bottle in this batch must resolve to the same
      // origin as the first bottle accepted this session (single or batch).
      if (originLock && effectiveOrigin !== originLock) {
        const found = effectiveOrigin === 'mother' ? t('common.mother') : t('common.donor');
        const expected = originLock === 'mother' ? t('common.mother') : t('common.donor');
        rejected.push({ id, reason: t('inventory.providerMismatch', { found, expected }) });
        continue;
      }

      // Same-provider check — every bottle in this batch must belong to the
      // same mother/donor as the first bottle accepted this session.
      if (providerLock && provider.person_id !== providerLock) {
        const label = props.origin === 'mother' ? t('common.mother') : t('common.donor');
        rejected.push({ id, reason: t('inventory.receive.differentProvider', { id, label }) });
        continue;
      }

      // Donor screening check: only APPROVED donors may have milk received.
      if (effectiveOrigin === 'donor' && provider.screening_status !== 'APPROVED') {
        rejected.push({ id, reason: t('inventory.donorScreeningNotApproved') });
        continue;
      }

      acceptedBottles.push({
        containerId: id,
        providerName: `${provider.first_name} ${provider.last_name}`,
        providerId: provider.person_id,
        scannedOrigin: effectiveOrigin,
      });
      acceptedSet.add(id);
      accepted.push(id);
      if (!originLock) originLock = effectiveOrigin;
      if (!providerLock) providerLock = provider.person_id;
    } catch (err: unknown) {
      const status = (err as { response?: { status?: number } })?.response?.status;
      rejected.push({ id, reason: status === 404 ? t('inventory.noProviderFound') : t('inventory.failedLookupProvider') });
    }
  }

  scannedBottles.value.push(...acceptedBottles);
  if (!firstScanOrigin.value && originLock) firstScanOrigin.value = originLock;
  isAdding.value = false;
  scanInput.value = '';
  batchResult.value = { accepted, rejected };
}

function removeScannedBottle(index: number) {
  scannedBottles.value.splice(index, 1);
  if (scannedBottles.value.length === 0) firstScanOrigin.value = null;
}

// Clear batch result only when user starts typing a new input (not when input is programmatically cleared)
watch(scanInput, (newVal) => {
  if (newVal && batchResult.value) batchResult.value = null;
});

// ---------------------------------------------------------------------------
// Step 2 — Row form state
// ---------------------------------------------------------------------------
interface BottleRow {
  containerId: string;
  personName: string;
  /** person_id from the scan step provider lookup */
  providerId: string | null;
  /** true when personName was pre-filled by provider lookup (field is read-only in Step 2) */
  providerReadOnly: boolean;
  volumeMl: number | null;
  milkType: string | null;
  milkStatus: string | null;
  donorStatus: DonorApprovalStatus | null;
  location: string;
  pumpedDate: Date | null;
  expiryDate: Date | null;
  isFetchingExpiry: boolean;
  errors: Record<string, string>;
}

const rows = ref<BottleRow[]>([]);
const submitted = ref(false);
const isSubmitting = ref(false);

function createRowFromBottle(bottle: ScannedBottle): BottleRow {
  return {
    containerId: bottle.containerId,
    personName: bottle.providerName,
    providerId: bottle.providerId,
    providerReadOnly: !!bottle.providerName,
    volumeMl: null,
    milkType: null,
    milkStatus: null,
    donorStatus: null,
    location: '',
    pumpedDate: null,
    expiryDate: null,
    isFetchingExpiry: false,
    errors: {},
  };
}

function goToDetails() {
  if (scannedBottles.value.length === 0) return;
  rows.value = scannedBottles.value.map(createRowFromBottle);
  submitted.value = false;
  currentStep.value = 'details';
}

function goBackToScan() {
  currentStep.value = 'scan';
  submitted.value = false;
}

// ---------------------------------------------------------------------------
// Per-row field change handlers (for expiry auto-calculation)
// ---------------------------------------------------------------------------
/**
 * Clears a row's location when it's THAWED and the location isn't Fridge-type
 * (including an unrecognized/missing storage_type), or when it's FROZEN and
 * the location IS Fridge-type — shared by the per-select change handler AND
 * applyToAll(), which assigns milkStatus/location directly and must not
 * bypass this normalization.
 */
function clearIncompatibleRowLocation(row: BottleRow) {
  if (!row.location) return;
  const opt = locationOptions.value.find((o) => o.value === row.location);
  const bucket = getLocationBucket(opt?.storageType);
  if (row.milkStatus === 'THAWED' && bucket !== 'Fridge') {
    row.location = '';
  } else if (row.milkStatus === 'FROZEN' && bucket === 'Fridge') {
    row.location = '';
  }
}

async function onRowMilkStatusChange(index: number) {
  const row = rows.value[index];
  if (!row.milkStatus) {
    row.expiryDate = null;
    return;
  }
  clearIncompatibleRowLocation(row);
  if (row.pumpedDate) {
    await recalcExpiryForRow(index);
  }
}

async function onRowPumpedDateChange(index: number) {
  const row = rows.value[index];
  if (row.milkStatus && row.pumpedDate) {
    await recalcExpiryForRow(index);
  }
}

/** Location is part of the rule lookup key (Origin + Type + Location) — recalculate on change. */
async function onRowLocationChange(index: number) {
  const row = rows.value[index];
  if (row.milkStatus && row.pumpedDate) {
    await recalcExpiryForRow(index);
  }
}

// ---------------------------------------------------------------------------
// Apply to All — copies non-empty fields from row 0 to all other rows
// ---------------------------------------------------------------------------
async function applyToAll() {
  if (rows.value.length < 2) return;

  const source = rows.value[0];
  const copiedFieldNames: string[] = [];
  const recalcIndices: number[] = [];

  for (let i = 1; i < rows.value.length; i++) {
    const target = rows.value[i];
    let needsRecalc = false;

    // Skip personName for rows whose provider was locked in during scanning
    if (source.personName.trim() && !target.providerReadOnly) {
      target.personName = source.personName;
      if (i === 1) copiedFieldNames.push(personLabel.value);
    }
    if (source.volumeMl !== null && source.volumeMl > 0) {
      target.volumeMl = source.volumeMl;
      if (i === 1) copiedFieldNames.push(t('feed.volume'));
    }
    if (source.milkType) {
      target.milkType = source.milkType;
      if (i === 1) copiedFieldNames.push(t('feed.milkType'));
    }
    if (source.milkStatus) {
      target.milkStatus = source.milkStatus;
      needsRecalc = true;
      if (i === 1) copiedFieldNames.push(t('inventory.milkStatus'));
    }
    if (source.location) {
      target.location = source.location;
      needsRecalc = true;
      if (i === 1) copiedFieldNames.push(t('common.location'));
    }
    // milkStatus/location were assigned directly above, bypassing the
    // per-select normalization — re-check now that both are settled.
    clearIncompatibleRowLocation(target);
    if (source.pumpedDate) {
      target.pumpedDate = new Date(source.pumpedDate.getTime());
      needsRecalc = true;
      if (i === 1) copiedFieldNames.push(t('inventory.pumpedDate'));
    }
    // Copy expiryDate only when we won't recalculate it
    if (source.expiryDate && !needsRecalc) {
      target.expiryDate = new Date(source.expiryDate.getTime());
    }

    if (needsRecalc) recalcIndices.push(i);
  }

  // Recalculate expiry for all affected rows
  await Promise.all(recalcIndices.map((i) => recalcExpiryForRow(i)));

  if (copiedFieldNames.length === 0) {
    toast.info(t('inventory.receive.noFieldsInBottle1'));
  } else {
    toast.success(
      t('inventory.receive.appliedFieldsToBottles', {
        fields: copiedFieldNames.join(', '),
        count: rows.value.length - 1,
      }),
    );
  }
}

// ---------------------------------------------------------------------------
// Validation
// ---------------------------------------------------------------------------
// Current moment used as the upper bound for Pumped Date — refreshed on dialog
// open and whenever a row's picker panel is shown so users can never select a
// future date *or* a future time later today.
const today = ref(new Date());

function refreshToday() {
  today.value = new Date();
}

const personLabel = computed(() => {
  const _lng = locale.value;
  return props.origin === 'mother'
    ? t('mother.name')
    : t('donor.name');
});

function validateRow(row: BottleRow): Record<string, string> {
  const errs: Record<string, string> = {};

  if (!row.personName.trim()) {
    errs.personName = t('inventory.personNameRequired', { label: personLabel.value });
  }
  if (row.volumeMl === null || row.volumeMl <= 0) {
    errs.volumeMl = t('inventory.volumePositive');
  }
  if (!row.milkType) {
    errs.milkType = t('inventory.milkTypeRequired');
  }
  if (!row.milkStatus) {
    errs.milkStatus = t('inventory.milkStatusRequired');
  }
  if (!row.location) {
    errs.location = t('inventory.locationRequired');
  } else if (row.milkStatus === 'THAWED' || row.milkStatus === 'FROZEN') {
    // Last-line-of-defense: catches an incompatible location that reached
    // this row through any path other than the select/applyToAll normalization.
    const opt = locationOptions.value.find((o) => o.value === row.location);
    const bucket = getLocationBucket(opt?.storageType);
    if (row.milkStatus === 'THAWED' && bucket !== 'Fridge') {
      errs.location = t('inventory.thawedLocationMustBeFridge');
    } else if (row.milkStatus === 'FROZEN' && bucket === 'Fridge') {
      errs.location = t('inventory.frozenLocationCannotBeFridge');
    }
  }
  if (!row.pumpedDate) {
    errs.pumpedDate = t('inventory.pumpedDateRequired');
  } else if (row.pumpedDate > today.value) {
    errs.pumpedDate = t('inventory.pumpedDateFuture');
  }
  if (!row.expiryDate) {
    errs.expiryDate = t('inventory.expiryDateRequired');
  } else if (row.pumpedDate && row.expiryDate <= row.pumpedDate) {
    errs.expiryDate = t('inventory.expiryDateAfterPumped');
  }

  return errs;
}

function validateAllRows(): boolean {
  let allValid = true;
  for (const row of rows.value) {
    row.errors = validateRow(row);
    if (Object.keys(row.errors).length > 0) allValid = false;
  }
  return allValid;
}

// ---------------------------------------------------------------------------
// Submit
// ---------------------------------------------------------------------------
async function handleReceive() {
  submitted.value = true;

  if (rows.value.some((row: BottleRow) => row.volumeMl !== null && row.volumeMl <= 0)) {
    toast.warn(t('inventory.volumePositive'));
    return;
  }

  if (!validateAllRows()) {
    toast.danger(t('inventory.fixValidationErrors'));
    return;
  }

  isSubmitting.value = true;
  try {
    const bottles: ReceiveMilkContainerPayload[] = rows.value.map((row: BottleRow) => {
      const resolvedStorageLocationId = locationOptions.value.find((o) => o.value === row.location)?.storageLocationId;
      if (props.origin === 'mother') {
        return {
          origin: 'mother',
          containerId: row.containerId,
          motherName: row.personName.trim(),
          providerId: row.providerId ?? undefined,
          volumeMl: row.volumeMl!,
          milkType: row.milkType! as MilkReceivableMilkType,
          status: row.milkStatus! as MilkReceivableMilkStatus,
          location: row.location,
          storageLocationId: resolvedStorageLocationId,
          pumpedDate:
            pickerDateToUtcIso(row.pumpedDate!) ?? row.pumpedDate!.toISOString(),
          expiryDate:
            pickerDateToUtcIso(row.expiryDate!) ?? row.expiryDate!.toISOString(),
        } satisfies ReceiveMotherMilkPayload;
      } else {
        return {
          origin: 'donor',
          containerId: row.containerId,
          donorName: row.personName.trim(),
          providerId: row.providerId ?? undefined,
          volumeMl: row.volumeMl!,
          milkType: row.milkType! as MilkReceivableMilkType,
          status: row.milkStatus! as MilkReceivableMilkStatus,
          donorStatus: row.donorStatus ?? 'Pending',
          location: row.location,
          storageLocationId: resolvedStorageLocationId,
          pumpedDate:
            pickerDateToUtcIso(row.pumpedDate!) ?? row.pumpedDate!.toISOString(),
          expiryDate:
            pickerDateToUtcIso(row.expiryDate!) ?? row.expiryDate!.toISOString(),
        } satisfies ReceiveDonorMilkPayload;
      }
    });

    const payload: ReceiveMultipleBottlesPayload = {
      origin: props.origin,
      bottles,
    };

    emit('containers-received', payload);
    resetDirty();
    resetAndClose();
  } finally {
    isSubmitting.value = false;
  }
}

// ---------------------------------------------------------------------------
// Dialog open / close lifecycle
// ---------------------------------------------------------------------------
function resetDialog() {
  currentStep.value = 'scan';
  scanInput.value = '';
  scannedBottles.value = [];
  scanInputError.value = '';
  isAdding.value = false;
  batchResult.value = null;
  firstScanOrigin.value = null;
  rows.value = [];
  submitted.value = false;
  isSubmitting.value = false;
  resetExpiryRulesCache();
  refreshToday();
}

function resetAndClose() {
  resetDialog();
  scanner.disconnectScanner();
  emit('update:visible', false);
}

async function handleClose() {
  if (!(await guardClose())) return;
  resetDirty();
  resetAndClose();
}

watch(
  () => props.visible,
  async (visible: boolean) => {
    if (visible) {
      resetDialog();
      fetchStorageLocations();
      loadMilkExpiryRuleRows();
      settingsStore.ensureMilkTypes();
      await scanner.connectScanner();
    } else {
      scanner.disconnectScanner();
      resetExpiryRulesCache();
    }
  },
  { immediate: true },
);

// ---------------------------------------------------------------------------
// Computed helpers
// ---------------------------------------------------------------------------
const dialogTitle = computed(() => {
  const _lng = locale.value;
  return props.origin === 'mother'
    ? t('inventory.receiveMultipleMotherMilk')
    : t('inventory.receiveMultipleDonorMilk');
});

const milkStatusOptionsForOrigin = computed(() => milkStatusOptions.value);

const hasValidationErrors = computed(() =>
  rows.value.some((row: BottleRow) => Object.keys(row.errors).length > 0),
);

const firstRowHasAnyField = computed(() => {
  if (!rows.value.length) return false;
  const r = rows.value[0];
  return (
    !!r.personName.trim() ||
    r.volumeMl !== null ||
    !!r.milkType ||
    !!r.milkStatus ||
    !!r.location ||
    !!r.pumpedDate
  );
});
</script>

<template>
  <DialogModal
    :visible="visible"
    data-testid="receive-multiple-milk-dialog"
    :title="dialogTitle"
    max-width="max-w-full"
    max-height="96vh"
    position="center"
    :show-close="true"
    no-padding
    intercept-close
    @update:visible="(v: boolean) => { if (!v) handleClose(); }"
    @close="handleClose"
  >
    <div class="px-4 py-3">
      <!-- ── Step indicator ────────────────────────────────────────────────── -->
      <div class="flex items-center gap-3 mb-6 px-1">
        <!-- Step 1 -->
        <div class="flex items-center gap-2">
          <span
            :class="[
              'flex items-center justify-center w-7 h-7 rounded-full text-xs font-bold shrink-0',
              currentStep === 'details'
                ? 'bg-green-500 text-white'
                : 'bg-primary text-white',
            ]"
          >
            <Icon
              v-if="currentStep === 'details'"
              name="CircleCheck"
              size-class="h-3.5 w-3.5"
              color="text-white"
              variant="stroke"
            />
            <span v-else>1</span>
          </span>
          <span
            :class="[
              'text-sm font-medium',
              currentStep === 'scan' ? 'text-gray-900' : 'text-gray-400',
            ]"
          >
            {{ $t('inventory.receive.scanBottles') }}
          </span>
        </div>

        <div class="flex-1 h-px bg-gray-200 mx-1" />

        <!-- Step 2 -->
        <div class="flex items-center gap-2">
          <span
            :class="[
              'flex items-center justify-center w-7 h-7 rounded-full text-xs font-bold shrink-0',
              currentStep === 'details'
                ? 'bg-primary text-white'
                : 'bg-gray-200 text-gray-500',
            ]"
          >
            2
          </span>
          <span
            :class="[
              'text-sm font-medium',
              currentStep === 'details' ? 'text-gray-900' : 'text-gray-400',
            ]"
          >
            {{ $t('inventory.receive.fillDetails') }}
          </span>
        </div>
      </div>

      <!-- ════════════════════════════════════════════════════════════════════ -->
      <!-- STEP 1: SCAN                                                        -->
      <!-- ════════════════════════════════════════════════════════════════════ -->
      <div
        v-if="currentStep === 'scan'"
        class="flex flex-col gap-5 overflow-y-auto max-h-[55vh] pr-1"
      >
        <!-- Instruction banner -->
        <div class="flex items-start gap-2 rounded-md bg-blue-50 border border-blue-200 px-4 py-3 text-sm text-blue-700">
          <Icon
            name="InfoCircle"
            size-class="h-4 w-4 shrink-0 mt-0.5"
            color="text-blue-900"
          />
          <span>
            {{ $t('inventory.receive.scanInstructionPart1') }}
            <strong>{{ $t('common.add') }}</strong>
            {{ $t('inventory.receive.scanInstructionPart2') }}
            <kbd class="px-1 py-0.5 rounded bg-blue-100 text-xs font-mono">Enter</kbd>
            {{ $t('inventory.receive.scanInstructionPart3') }}
            <strong>{{ $t('common.next') }}</strong>.
          </span>
        </div>

        <!-- Scan input row -->
        <div class="flex flex-col gap-1">
          <label class="text-sm font-medium text-gray-700">
            {{ $t('inventory.containerId') }}
            <span class="text-gray-500 font-normal text-xs ml-1">
              {{ $t('inventory.scanBarcodeOrEnterManually') }}
            </span>
          </label>
          <div class="flex gap-2">
            <ScanInput
              v-model="scanInput"
              :scanner="scanner"
              data-testid="multi-scan-input"
              :placeholder="$t('inventory.scanOrTypeId')"
              :maxlength="1000"
              :invalid="!!scanInputError"
              :disabled="scannedBottles.length >= MAX_BOTTLES"
              wrapper-class="flex-1"
              @scan="addScannedBottle"
            />
            <Button
              :label="t('common.add')"
              data-testid="multi-add-btn"
              severity="secondary"
              outlined
              class="shrink-0"
              icon="pi pi-plus"
              :loading="isAdding"
              :disabled="isAdding || scannedBottles.length >= MAX_BOTTLES"
              @click="addScannedBottle"
            />
          </div>
          <small
            v-if="scannedBottles.length >= MAX_BOTTLES && !scanInputError"
            class="text-amber-600 text-xs"
          >
            {{ $t('inventory.bottleLimitReached', { max: MAX_BOTTLES }) }}
          </small>
          <small
            v-if="scanInputError"
            class="text-danger text-xs"
          >
            {{ scanInputError }}
          </small>
        </div>

        <!-- Batch paste result panel -->
        <div
          v-if="batchResult"
          class="rounded-md border text-xs"
          :class="batchResult.rejected.length ? 'border-red-200 bg-red-50' : 'border-green-200 bg-green-50'"
        >
          <!-- Summary row -->
          <div class="flex items-center gap-4 px-3 py-2">
            <span
              v-if="batchResult.accepted.length"
              class="flex items-center gap-1 font-medium text-green-700"
            >
              <Icon
                name="CircleCheck"
                size-class="h-3.5 w-3.5 shrink-0"
                color="text-green-600"
                variant="stroke"
              />
              {{ batchResult.accepted.length }} {{ $t('inventory.receive.statusAccepted') }}
            </span>
            <span
              v-if="batchResult.rejected.length"
              class="flex items-center gap-1 font-medium text-red-600"
            >
              <Icon
                name="Close"
                size-class="h-3.5 w-3.5 shrink-0"
                color="text-gray-600"
              />
              {{ batchResult.rejected.length }} {{ $t('milk.actionRejectedPast') }}
            </span>
          </div>
          <!-- Rejected chips -->
          <div
            v-if="batchResult.rejected.length"
            class="border-t border-red-200 px-3 py-2 flex flex-wrap gap-2"
          >
            <div
              v-for="r in batchResult.rejected"
              :key="r.id"
              class="inline-flex items-start gap-1.5 rounded-lg bg-white border border-red-200 shadow-sm px-2.5 py-1.5 max-w-[260px]"
            >
              <div class="flex flex-col leading-tight min-w-0">
                <span class="text-xs font-semibold text-gray-800 truncate">{{ r.id }}</span>
                <span class="text-xs text-red-500 whitespace-normal break-words">{{ r.reason }}</span>
              </div>
            </div>
          </div>
        </div>

        <!-- Scanned bottles — chips display -->
        <div class="flex flex-col gap-2">
          <div class="flex items-center justify-between">
            <span class="text-sm font-medium text-gray-700">{{ $t('inventory.receive.scannedBottles') }}</span>
            <Tag
              v-if="scannedBottles.length"
              :value="$t('inventory.receive.bottlesCount', { count: scannedBottles.length })"
              severity="info"
              class="text-xs"
            />
          </div>

          <!-- Chips area: shows placeholder when empty, chips otherwise -->
          <div
            class="flex flex-wrap gap-2 rounded-md border border-gray-200 bg-gray-50 p-3 min-h-[72px] content-start"
            :class="{ 'items-center justify-center': !scannedBottles.length }"
          >
            <!-- Empty placeholder -->
            <div
              v-if="!scannedBottles.length"
              class="flex items-center gap-2 text-gray-400"
            >
              <Icon
                name="QrCodeScanner"
                size-class="h-5 w-5"
                color="text-gray-300"
              />
              <span class="text-sm">{{ $t('inventory.receive.scannedBottlesEmpty') }}</span>
            </div>

            <!-- One chip per scanned bottle -->
            <div
              v-for="(bottle, idx) in scannedBottles"
              :key="bottle.containerId"
              class="inline-flex items-center gap-1.5 rounded-full bg-white border border-primary/30 shadow-sm px-2.5 py-1 max-w-[220px]"
            >
              <span class="flex items-center justify-center w-4 h-4 rounded-full bg-primary/10 text-primary text-xs font-bold shrink-0">
                {{ (idx as number) + 1 }}
              </span>
              <div class="flex flex-col leading-tight min-w-0 overflow-hidden">
                <span class="text-xs font-semibold text-gray-800 truncate">
                  {{ bottle.providerName || bottle.containerId }}
                </span>
                <span
                  v-if="bottle.providerName"
                  class="text-xs text-gray-400 truncate"
                >
                  {{ bottle.containerId }}
                </span>
              </div>
              <button
                type="button"
                :data-testid="`remove-bottle-${idx}`"
                class="shrink-0 ml-0.5 p-0.5 rounded-full text-gray-400 hover:bg-red-50 hover:text-red-500 transition-colors"
                @click="removeScannedBottle(idx as number)"
              >
                <Icon
                  name="Close"
                  size-class="h-3 w-3"
                  color="text-gray-600"
                />
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- ════════════════════════════════════════════════════════════════════ -->
      <!-- STEP 2: DETAILS                                                     -->
      <!-- ════════════════════════════════════════════════════════════════════ -->
      <div
        v-else
        class="flex flex-col gap-4"
      >
        <!-- Apply to All action bar -->
        <div class="flex items-center justify-between rounded-md bg-amber-50 border border-amber-200 px-4 py-3">
          <div class="flex items-start gap-2 text-sm text-amber-700">
            <Icon
              name="CircleExclamation"
              size-class="h-4 w-4 shrink-0 mt-0.5"
              color="text-amber-600"
            />
            <span>{{ $t('inventory.receive.applyToAllHint') }}</span>
          </div>
          <Button
            :label="$t('inventory.receive.applyToAll')"
            data-testid="apply-to-all-btn"
            severity="primary"
            size="small"
            class="shrink-0 ml-3"
            :disabled="!firstRowHasAnyField || rows.length < 2"
            @click="applyToAll"
          />
        </div>

        <!-- Validation summary banner (shown only after first submit attempt) -->
        <div
          v-if="submitted && hasValidationErrors"
          class="flex items-start gap-2 rounded-md bg-red-50 border border-red-300 px-4 py-3 text-sm text-red-700"
        >
          <Icon
            name="CircleExclamation"
            size-class="h-4 w-4 shrink-0 mt-0.5"
            color="text-red-600"
          />
          <span>{{ $t('inventory.receive.someRowsInvalid') }}</span>
        </div>

        <!-- Bottle rows — scrollable table -->
        <div class="overflow-x-auto max-h-[57vh] overflow-y-auto rounded-lg border border-gray-200 pb-3">
          <DataTable
            :value="rows"
            data-key="containerId"
            class="w-full text-sm border-collapse"
            scrollable
            scroll-height="57vh"
            :row-class="(data: typeof rows[number]) => submitted && Object.keys(data.errors).length ? 'bg-red-50/40' : 'bg-white hover:bg-gray-50/60'"
          >
            <Column style="width: 2.5rem">
              <template #header>
                <span>#</span>
              </template>
              <template #body="{ index }">
                <span class="flex items-center justify-center w-6 h-6 rounded-full bg-primary text-white text-xs font-bold">
                  {{ (index as number) + 1 }}
                </span>
              </template>
            </Column>

            <Column
              field="containerId"
              style="min-width: 130px"
            >
              <template #header>
                <span>{{ $t('inventory.containerId') }}</span>
              </template>
              <template #body="{ data }">
                <span class="text-xs font-medium text-gray-800 break-all">{{ data.containerId }}</span>
              </template>
            </Column>

            <Column
              field="personName"
              style="min-width: 150px"
            >
              <template #header>
                <span>{{ personLabel }} <span class="text-danger">*</span></span>
              </template>
              <template #body="{ data, index }">
                <InputText
                  v-model="data.personName"
                  :data-testid="`person-name-${index}`"
                  :placeholder="origin === 'mother' ? 'Mother name' : 'Donor name'"
                  class="w-full"
                  :class="{ 'p-invalid': submitted && data.errors.personName }"
                  :readonly="data.providerReadOnly"
                  size="small"
                />
                <small
                  v-if="submitted && data.errors.personName"
                  class="text-danger text-xs block"
                >
                  {{ data.errors.personName }}
                </small>
              </template>
            </Column>

            <Column
              field="volumeMl"
              style="min-width: 90px"
            >
              <template #header>
                <span>{{ $t('feed.volume') }} <span class="text-danger">*</span></span>
              </template>
              <template #body="{ data, index }">
                <InputNumber
                  v-model="data.volumeMl"
                  :data-testid="`volume-${index}`"
                  :placeholder="$t('common.milkUnit')"
                  :max="9999"
                  :use-grouping="false"
                  class="w-full"
                  :input-class="submitted && data.errors.volumeMl ? 'p-invalid w-full' : 'w-full'"
                  :input-style="{ padding: '0.375rem 0.5rem', fontSize: '0.875rem' }"
                />
                <small
                  v-if="submitted && data.errors.volumeMl"
                  class="text-danger text-xs block"
                >
                  {{ data.errors.volumeMl }}
                </small>
              </template>
            </Column>

            <Column
              field="milkType"
              style="min-width: 140px"
            >
              <template #header>
                <span>{{ $t('feed.milkType') }} <span class="text-danger">*</span></span>
              </template>
              <template #body="{ data, index }">
                <Select
                  v-model="data.milkType"
                  :data-testid="`milk-type-${index}`"
                  :options="milkTypeApiOptions"
                  option-label="label"
                  option-value="value"
                  :placeholder="$t('common.select')"
                  :loading="isMilkTypeLoading"
                  class="w-full"
                  :class="{ 'p-invalid': submitted && data.errors.milkType }"
                />
                <small
                  v-if="submitted && data.errors.milkType"
                  class="text-danger text-xs block"
                >
                  {{ data.errors.milkType }}
                </small>
              </template>
            </Column>

            <Column
              field="milkStatus"
              style="min-width: 140px"
            >
              <template #header>
                <span>{{ $t('inventory.milkStatus') }} <span class="text-danger">*</span></span>
              </template>
              <template #body="{ data, index }">
                <Select
                  v-model="data.milkStatus"
                  :data-testid="`milk-status-${index}`"
                  :options="milkStatusOptionsForOrigin"
                  option-label="label"
                  option-value="value"
                  :placeholder="$t('common.select')"
                  class="w-full"
                  :class="{ 'p-invalid': submitted && data.errors.milkStatus }"
                  @update:model-value="onRowMilkStatusChange(index as number)"
                />
                <small
                  v-if="submitted && data.errors.milkStatus"
                  class="text-danger text-xs block"
                >
                  {{ data.errors.milkStatus }}
                </small>
              </template>
            </Column>

            <!-- Location -->
            <Column
              field="location"
              style="min-width: 140px"
            >
              <template #header>
                <span>{{ $t('common.location') }} <span class="text-danger">*</span></span>
              </template>
              <template #body="{ data, index }">
                <Select
                  v-model="data.location"
                  :data-testid="`location-${index}`"
                  :options="getLocationOptionsForRow(data.milkStatus)"
                  option-label="label"
                  option-value="value"
                  :placeholder="$t('common.select')"
                  :loading="isLocationLoading"
                  class="w-full"
                  :class="{ 'p-invalid': submitted && data.errors.location }"
                  @update:model-value="onRowLocationChange(index as number)"
                />
                <small
                  v-if="submitted && data.errors.location"
                  class="text-danger text-xs block"
                >
                  {{ data.errors.location }}
                </small>
              </template>
            </Column>

            <Column
              field="pumpedDate"
              style="min-width: 175px"
            >
              <template #header>
                <span>{{ $t('inventory.pumpedDate') }} <span class="text-danger">*</span></span>
              </template>
              <template #body="{ data, index }">
                <AppDatePicker
                  v-model="data.pumpedDate"
                  :data-testid="`pumped-date-${index}`"
                  :placeholder="$t('inventory.datePlaceholderDmyHm')"
                  date-format="dd/mm/yy"
                  :max-date="today"
                  show-icon
                  show-time
                  hour-format="24"
                  class="w-full"
                  :class="{ 'p-invalid': submitted && data.errors.pumpedDate }"
                  @update:model-value="onRowPumpedDateChange(index as number)"
                  @show="refreshToday"
                />
                <small
                  v-if="submitted && data.errors.pumpedDate"
                  class="text-danger text-xs block"
                >
                  {{ data.errors.pumpedDate }}
                </small>
              </template>
            </Column>

            <Column
              field="expiryDate"
              style="min-width: 175px"
            >
              <template #header>
                <span>{{ $t('milk.expiryDate') }} <span class="text-danger">*</span></span>
              </template>
              <template #body="{ data, index }">
                <div class="relative">
                  <AppDatePicker
                    v-model="data.expiryDate"
                    :data-testid="`expiry-date-${index}`"
                    :placeholder="data.isFetchingExpiry ? 'Calculating…' : $t('inventory.datePlaceholderDmyHm')"
                    date-format="dd/mm/yy"
                    show-icon
                    show-time
                    hour-format="24"
                    disabled
                    class="w-full"
                    :class="{ 'p-invalid': submitted && data.errors.expiryDate }"
                  />
                  <div
                    v-if="data.isFetchingExpiry"
                    class="absolute right-9 top-1/2 -translate-y-1/2"
                  >
                    <Icon
                      name="RefreshData"
                      size-class="h-3.5 w-3.5 animate-spin"
                      color="text-gray-400"
                      variant="stroke"
                    />
                  </div>
                </div>
                <small
                  v-if="submitted && data.errors.expiryDate"
                  class="text-danger text-xs block"
                >
                  {{ data.errors.expiryDate }}
                </small>
              </template>
            </Column>
          </DataTable>
        </div>
      </div>
    </div>

    <!-- ── Footer ────────────────────────────────────────────────────────── -->
    <template #footer>
      <!-- Step 1 footer -->
      <div
        v-if="currentStep === 'scan'"
        class="w-full flex items-center justify-between"
      >
        <span class="text-sm text-gray-500">
          <template v-if="scannedBottles.length">
            {{ $t('inventory.receive.bottleReady', { count: scannedBottles.length }) }}
          </template>
          <template v-else>
            {{ $t('inventory.receive.scanAtLeastOne') }}
          </template>
        </span>
        <div class="flex gap-3">
          <Button
            :label="$t('common.cancel')"
            severity="secondary"
            outlined
            data-testid="multi-cancel-scan-btn"
            @click="handleClose"
          />
          <Button
            :label="$t('common.next')"
            severity="primary"
            data-testid="multi-next-btn"
            :disabled="scannedBottles.length === 0 || isAdding"
            @click="goToDetails"
          >
            <template #icon>
              <Icon
                name="AngleRight"
                size-class="h-4 w-4"
                color="text-white"
              />
            </template>
          </Button>
        </div>
      </div>

      <!-- Step 2 footer -->
      <div
        v-else
        class="w-full flex items-center justify-between"
      >
        <span class="text-sm text-gray-500">
          {{ $t('inventory.receive.bottleToReceive', { count: rows.length }) }}
        </span>
        <div class="flex gap-3">
          <Button
            :label="$t('common.back')"
            severity="secondary"
            outlined
            data-testid="multi-back-btn"
            :disabled="isSubmitting"
            @click="goBackToScan"
          >
            <template #icon>
              <Icon
                name="Angle"
                size-class="h-4 w-4"
                color="text-gray-600"
              />
            </template>
          </Button>
          <Button
            :label="$t('donor.actions.receive')"
            severity="primary"
            data-testid="multi-receive-btn"
            :loading="isSubmitting"
            @click="handleReceive"
          >
            <template #icon>
              <Icon
                name="CircleCheck"
                size-class="h-4 w-4"
                color="text-white"
                variant="stroke"
              />
            </template>
          </Button>
        </div>
      </div>
    </template>
  </DialogModal>

  <FormDirtyDialog
    :visible="confirmState.visible"
    @confirm="onConfirm"
    @cancel="onCancel"
    @update:visible="onVisibleUpdate"
  />
</template>
